import { DomainProductLine, InputFilter } from 'types/domainService';
import { Cart } from 'types/cartService';
import { action, computed, makeObservable, observable } from 'mobx';
import { getDomainProductLineCached } from 'api/operations/domainServiceV2';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import {
  PRODUCT_LINES_CONTENT_MAP,
  sortByProductLineId,
} from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';

export default class ProductLineModule {
  /** List of all available product lines based on the domain configuration */
  private _domainProductLines: DomainProductLine[] = [];

  private _setDomainProductLines(productLines: DomainProductLine[]): void {
    this._domainProductLines = productLines;
  }

  // Returns all available Product Lines based on the Domain Configuration
  public get allSortedProductLineIds(): ProductLineId[] {
    return this._domainProductLines.map(({ productLineId }) => productLineId).sort(sortByProductLineId);
  }

  // Display additional benefits for current product line ids
  public getUnusedProductLineIds(selectedProductLineIds: ProductLineId[]): ProductLineId[] {
    return this.allSortedProductLineIds
      .filter((productLineId) => {
        return !selectedProductLineIds.includes(productLineId);
      })
      .sort(sortByProductLineId);
  }

  /**
   * Function marks product lines as "invalid" based on the special business rules
   * Currently it has only one additional rule from DDWA - dependent product lines, when the user has to elect one product line to be eligible to elect into another
   * Example: DDWA has a rule that obliges user to add Dental product to the cart to be eligible to add Vision product, so Vision only cart is not valid
   * */
  public checkCarrierProductLinesRules(selectedProductLineIds: ProductLineId[]): Record<string, string>[] {
    const invalidProductLines: Record<string, string>[] = [];

    selectedProductLineIds.forEach((pLineId) => {
      const currentDomainPLine = this._domainProductLines.find((domainPLine) => domainPLine.productLineId === pLineId);
      if (!currentDomainPLine?.dependentOnProductLineId) return; // Skip product lines without dependencies

      const isDependentPLineSelected = selectedProductLineIds.includes(currentDomainPLine.dependentOnProductLineId);

      if (!isDependentPLineSelected) {
        invalidProductLines.push({
          [pLineId]: `Per carrier rules, you cannot proceed with only the following product selection. All selections must be paired with a ${
            PRODUCT_LINES_CONTENT_MAP[currentDomainPLine.dependentOnProductLineId].label
          } plan.`,
        });
      }
    });

    return invalidProductLines;
  }

  // Request to the DomainService to collect Product Line data
  public async getProductLines(cart: Cart, sponsorId: string): Promise<void> {
    const inputFilter: InputFilter = {
      effectiveDate: cart.lastUpdateBrowsingCriteria.effectiveDate,
      zipCode: cart.lastUpdateBrowsingCriteria.zipCode,
      state: cart.lastUpdateBrowsingCriteria.state,
      county: cart.lastUpdateBrowsingCriteria.county,
      agencyId: '',
      sponsorId,
    };

    const productLineIds = (await getDomainProductLineCached(inputFilter)).filter(
      (pLine) => pLine.rateSegment === cart.rateSegment,
    );
    this._setDomainProductLines(productLineIds);
  }

  constructor() {
    makeObservable<ProductLineModule, '_domainProductLines' | '_setDomainProductLines'>(this, {
      _domainProductLines: observable,

      allSortedProductLineIds: computed,

      _setDomainProductLines: action.bound,
      getProductLines: action.bound,
      getUnusedProductLineIds: action.bound,
      checkCarrierProductLinesRules: action.bound,
    });
  }
}
